import {
  useActualiseBasket,
  useActualiseTakeout,
  useActualiseBanners,
  useActualiseSettings,
  useActualiseDocuments,
  useActualiseCityCoords,
  useActualiseDeliveryTypes,
  useActualisePhoneCountryCode,
  useActualiseWbIntegration,
  useActualiseOptimalDeliveryZoneOnLocations,
} from './actualise';
import { useMemo } from 'react';
import { CatalogData } from '@/app/context';
import { useGetApiInfo } from './useGetApiInfo';
import { useAppSelector } from '@/app/store';
import { ClientMenuItemDto, ServiceTypeEnum } from '@/app/store/api/restApi';

export const useActualiseApp = () => {
  const {
    banners,
    menuItems,
    locations,
    categories,
    isLoading,
    isFetching,
    catalogError,
    bannersLoading,
    locationsLoading,
    menuItemsLoading,
    locationsRefetch,
    menuItemsRefetch,
    categoriesRefetch,
    setIsInitialMenuRefetch,
    setIsInitialCategoriesRefetch,
    reservationUrl,
  } = useGetApiInfo();
  const { chainInfoRefetch, isChainInfoFetching } = useActualiseSettings();

  useActualiseDocuments();
  useActualiseWbIntegration();
  useActualiseBanners({ banners, bannersLoading });
  useActualiseBasket({ menuItems, menuItemsLoading, categories });
  useActualiseTakeout({ locations, locationsLoading });
  useActualiseCityCoords({ locations, locationsLoading });
  useActualiseDeliveryTypes({ locations, locationsLoading });
  useActualisePhoneCountryCode({ locations, locationsLoading });
  useActualiseOptimalDeliveryZoneOnLocations({ locations, locationsLoading }); // для обновления locationId

  const deliveryType = useAppSelector((state) => state.order.deliveryType);
  const isCdek = deliveryType === ServiceTypeEnum.OrderPoint;

  // Authoritative "temporary category" signal: the category has a display window.
  // (The item's `isWithinDisplayInterval` flag only tells us if it is *currently*
  // inside that window — it is not a reliable marker of a temporary category.)
  const temporaryCategoryIds = useMemo(
    () => new Set(categories.filter((c) => c.displayInterval).map((c) => c.id)),
    [categories],
  );

  // Under CDEK every time-limited ("temporary") category item is unavailable,
  // regardless of the current time window, because CDEK ships over several days.
  const isDisabledItem = (mi: ClientMenuItemDto) =>
    mi.isWithinDisplayInterval === false || (isCdek && temporaryCategoryIds.has(mi.categoryId));

  return {
    banners,
    locations,
    menuItems: menuItems.filter((mi) => !isDisabledItem(mi)),
    categories,
    catalogError,
    chainInfoRefetch,
    locationsRefetch,
    menuItemsRefetch,
    categoriesRefetch,
    setIsInitialMenuRefetch,
    setIsInitialCategoriesRefetch,
    reservationUrl,
    isCatalogLoading: isLoading,
    isBasketFetching: isFetching || isChainInfoFetching,
  } as CatalogData;
};
